// Round-trip puro de `descripcion_json` para el formulario de planes.
//
// Extraído de PlanesForm.jsx para poder testear sin renderizar el formulario
// completo (que arrastra supabase/clerk/router). La lógica es idéntica a la que
// vivía inline en el componente: mismos defaults, mismo mapeo de
// campos_opcionales desde el catálogo, mismo serialize/parse de calculatorConfig.
//
// - defaultJsonConfig()        -> estado inicial de jsonConfig
// - parseDescripcionJson(str)  -> jsonConfig reconstruido desde el string JSON
// - buildDescripcionJson(cfg)  -> objeto listo para JSON.stringify hacia formData
//
// Sin React: importable desde tests puros y desde PlanesForm.

import {
  parse as parseCalculatorConfig,
  serialize as serializeCalculatorConfig,
  defaultsFor as calculatorDefaultsFor,
} from "../../components/tabs/calculator/calculatorConfigSchema.js";

/**
 * Frecuencia de la prima derivada de las filas de precios del plan.
 *
 * POR QUÉ EXISTE. La periodicidad se declaraba en dos lados: `precios_base.frecuencia`
 * —que es donde el admin ya la edita, pegada a `n_cuotas`, y lo que flow y payment
 * leen para armar las cuotas— y `descripcion_json.frecuenciaPrima`, que lee el
 * carrito de flow (ver su `frecuencia-prima.config.ts`) y que hasta ahora había que
 * escribir a mano en la base. Dos verdades sobre el mismo hecho: la del carrito se
 * quedaba en "mensual" y un pago único anual aparecía como cuota.
 *
 * Manda `precios_base`. Aquí solo se deriva el espejo, así que no queda nada que
 * mantener sincronizado a mano.
 *
 * Devuelve null cuando NO se puede afirmar: sin filas activas, o con filas que se
 * contradicen. En ese caso no se inventa un valor; el formulario lo avisa.
 */
export function frecuenciaPrimaDesdePrecios(preciosBase) {
  const activas = (preciosBase || []).filter((p) => p?.Activo ?? true);
  if (activas.length === 0) return null;

  // Sin declarar, una fila es mensual: es el default del programa de cobro.
  const frecuencias = new Set(
    activas.map((p) => (p?.frecuencia === "anual" ? "anual" : "mensual"))
  );
  return frecuencias.size === 1 ? [...frecuencias][0] : null;
}

// Estado inicial de jsonConfig (idéntico al literal que estaba en useState).
export function defaultJsonConfig() {
  return {
    tipo: "",
    badge: {
      tipo: "",
      texto: "",
      visible: true
    },
    capital: "",
    codigo_plan: "",
    featuresCard: [],
    restricciones: {
      edad_hijo: { min: 0, max: 30 },
      edad_conyuge: { min: 18, max: 70 },
      edad_titular: { min: 18, max: 70 }
    },
    catalogo_campos: [],
    campos_opcionales: [],
    aplica_cargas: true,
    modulos_requeridos: [],
    calculatorConfig: calculatorDefaultsFor("tramo_edad"),
    sugerencia: { participa: true, nota: "" }
  };
}

// Reconstruye jsonConfig desde el string JSON persistido en descripcion_json.
// Puede lanzar si el string no es JSON válido (igual que el JSON.parse original);
// el llamador (PlanesForm) mantiene el try/catch.
export function parseDescripcionJson(jsonString) {
  const parsed = JSON.parse(jsonString);
  return {
    tipo: parsed.tipo || "",
    badge: {
      tipo: parsed.badge?.tipo || "",
      texto: parsed.badge?.texto || "",
      visible: parsed.badge?.visible ?? true
    },
    capital: parsed.capital || "",
    codigo_plan: parsed.codigo_plan || "",
    // Las 3 frases cortas que muestra la card de resultado del miniform en flow.
    // Tiene que estar acá: este parse es un whitelist, y lo que no se lee se
    // borra al guardar (le pasó a frecuenciaPrima, ver el comentario más abajo).
    featuresCard: Array.isArray(parsed.featuresCard) ? parsed.featuresCard : [],
    restricciones: {
      edad_hijo: {
        min: parsed.restricciones?.edad_hijo?.min || 0,
        max: parsed.restricciones?.edad_hijo?.max || 30
      },
      edad_conyuge: {
        min: parsed.restricciones?.edad_conyuge?.min || 18,
        max: parsed.restricciones?.edad_conyuge?.max || 70
      },
      edad_titular: {
        min: parsed.restricciones?.edad_titular?.min || 18,
        max: parsed.restricciones?.edad_titular?.max || 70
      }
    },
    catalogo_campos: parsed.catalogo_campos || [
      { campo: "prevision", label: "Previsión", opciones: ["FONASA", "ISAPRE"] },
      { campo: "sexo", label: "Sexo", opciones: ["Masculino", "Femenino"] }
    ],
    campos_opcionales: parsed.campos_opcionales ?? [],
    aplica_cargas: parsed.aplica_cargas ?? true,
    modulos_requeridos: parsed.modulos_requeridos || [],
    // Se conserva aunque el formulario no la edite: el valor lo manda `precios_base`
    // y `buildDescripcionJson` lo recalcula. Antes no se leía, así que abrir un plan
    // y guardarlo BORRABA la clave y el carrito volvía a tratar la prima como mensual.
    ...(parsed.frecuenciaPrima ? { frecuenciaPrima: parsed.frecuenciaPrima } : {}),
    calculatorConfig: parseCalculatorConfig(parsed.calculatorConfig),
    sugerencia: {
      participa: parsed.sugerencia?.participa ?? true,
      nota: parsed.sugerencia?.nota ?? ""
    }
  };
}

// Arma el objeto descripcion_json a partir de jsonConfig (antes de JSON.stringify).
// Idéntico al build del useEffect de serialización: enriquece campos_opcionales
// con el `tipo` del catálogo y serializa calculatorConfig.
export function buildDescripcionJson(jsonConfig, preciosBase) {
  const catalogo = jsonConfig?.catalogo_campos || [];
  const camposOpcionales = (jsonConfig?.campos_opcionales || []).map(co => {
    const def = catalogo.find(c => c.campo === co.campo);
    return def?.tipo ? { ...co, tipo: def.tipo } : co;
  });

  // `frecuenciaPrima` es el espejo de `precios_base.frecuencia`, no un campo del
  // formulario: se recalcula en cada guardado. Si los precios no permiten afirmarla
  // (sin filas activas o contradictorias) se conserva la que ya tenía el plan, para
  // no borrar en silencio algo que el carrito está leyendo.
  const derivada = frecuenciaPrimaDesdePrecios(preciosBase);
  const frecuenciaPrima = derivada ?? jsonConfig?.frecuenciaPrima;

  return {
    ...jsonConfig,
    campos_opcionales: camposOpcionales,
    calculatorConfig: serializeCalculatorConfig(jsonConfig?.calculatorConfig),
    ...(frecuenciaPrima ? { frecuenciaPrima } : {}),
  };
}
